import { toast as sonnerToast } from 'svelte-sonner';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastAction {
	label: string;
	onClick: () => void;
}

export interface Toast {
	id: number;
	message: string;
	variant: ToastVariant;
	persistent: boolean;
	action?: ToastAction;
}

let nextId = 0;

function toSonnerId(id: number): string | number {
	return `toast-${id}`;
}

// Use a large finite value instead of Infinity for persistent toasts.
// sonner's update path calls setTimeout(fn, duration) — browsers treat
// setTimeout(fn, Infinity) as setTimeout(fn, 0), dismissing immediately.
const PERSISTENT_DURATION = 24 * 60 * 60 * 1000; // 24 hours

function showSonner(message: string, variant: ToastVariant, options?: {
	persistent?: boolean;
	action?: ToastAction;
	sonnerId?: string | number;
	dedupe?: boolean;
	description?: string;
	duration?: number;
	onDismiss?: () => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	componentDescription?: { component: any; props: Record<string, unknown> };
}): void {
	const sonnerOptions: Record<string, unknown> = {
		id: options?.sonnerId ?? (options?.dedupe ? `dedupe-${variant}-${message}` : undefined),
		duration: options?.duration ?? (options?.persistent ? PERSISTENT_DURATION : variant === 'error' ? 10000 : 4000),
	};

	if (options?.action) {
		sonnerOptions.action = {
			label: options.action.label,
			onClick: options.action.onClick
		};
	}

	if (options?.onDismiss) {
		sonnerOptions.onDismiss = options.onDismiss;
	}

	if (options?.componentDescription) {
		sonnerOptions.description = options.componentDescription.component;
		sonnerOptions.componentProps = options.componentDescription.props;
	} else if (options?.description) {
		sonnerOptions.description = options.description;
	}

	switch (variant) {
		case 'success':
			sonnerToast.success(message, sonnerOptions);
			break;
		case 'error':
			sonnerToast.error(message, sonnerOptions);
			break;
		case 'info':
			sonnerToast.info(message, sonnerOptions);
			break;
	}
}

function createToastStore() {
	return {
		success(message: string, description?: string, options?: { dedupe?: boolean }): void {
			showSonner(message, 'success', { description, dedupe: options?.dedupe });
		},
		error(message: string, description?: string, options?: { dedupe?: boolean }): void {
			showSonner(message, 'error', { description, dedupe: options?.dedupe });
		},
		info(message: string, options?: string | { description?: string; duration?: number; dedupe?: boolean; componentDescription?: { component: any; props: Record<string, unknown> } }): void {
			const opts = typeof options === 'string' ? { description: options } : options;
			showSonner(message, 'info', opts);
		},
		addPersistent(
			message: string,
			variant: ToastVariant,
			options?: string | {
				description?: string;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				componentDescription?: { component: any; props: Record<string, unknown> };
				action?: ToastAction;
				onDismiss?: () => void;
			}
		): number {
			const id = nextId++;
			const opts = typeof options === 'string' ? { description: options } : (options ?? {});
			showSonner(message, variant, { persistent: true, sonnerId: toSonnerId(id), ...opts });
			return id;
		},
		update(
			id: number,
			message: string,
			variant?: ToastVariant,
			options?: {
				action?: ToastAction;
				description?: string;
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				componentDescription?: { component: any; props: Record<string, unknown> };
			}
		): void {
			showSonner(message, variant ?? 'info', {
				persistent: true,
				sonnerId: toSonnerId(id),
				action: options?.action,
				description: options?.description,
				componentDescription: options?.componentDescription
			});
		},
		dismiss(id: number): void {
			sonnerToast.dismiss(toSonnerId(id));
		}
	};
}

export const toast = createToastStore();
